'use client'

import React from 'react'
import { useTenant } from '@/contexts/TenantContext'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import { 
  CheckCircle, 
  Clock, 
  Settings, 
  Users, 
  Package,
  CreditCard,
  Store,
  ArrowRight
} from 'lucide-react'

interface OnboardingDashboardWidgetProps {
  onActionClick?: (action: string) => void
}

export const OnboardingDashboardWidget: React.FC<OnboardingDashboardWidgetProps> = ({
  onActionClick
}) => {
  const { tenant } = useTenant()

  if (!tenant || tenant.onboarding_status === 'complete') {
    return null
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'registration':
        return 'bg-blue-100 text-blue-800'
      case 'verification':
        return 'bg-yellow-100 text-yellow-800'
      case 'setup':
        return 'bg-orange-100 text-orange-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'registration':
        return <Store className="h-4 w-4" />
      case 'verification':
        return <Clock className="h-4 w-4" />
      case 'setup':
        return <Settings className="h-4 w-4" />
      default:
        return <Store className="h-4 w-4" />
    }
  }

  const getProgressPercentage = () => {
    switch (tenant.onboarding_status) {
      case 'registration':
        return 25
      case 'verification':
        return 50
      case 'setup':
        return 75
      default:
        return 0
    }
  }

  const getNextAction = () => {
    switch (tenant.onboarding_status) {
      case 'registration':
        return {
          title: 'Complete Registration',
          description: 'Verify your email and complete account setup',
          action: 'registration',
          icon: Store
        }
      case 'verification':
        return {
          title: 'Verify Email',
          description: 'Check your email and click the verification link',
          action: 'verification',
          icon: Clock
        }
      case 'setup':
        return {
          title: 'Complete Setup',
          description: 'Configure your store settings and add initial data',
          action: 'setup',
          icon: Settings
        }
      default:
        return {
          title: 'Start Onboarding',
          description: 'Begin setting up your store',
          action: 'onboarding',
          icon: Store
        }
    }
  }

  const nextAction = getNextAction()
  const Icon = nextAction.icon

  return (
    <Card className="border-blue-200 bg-blue-50">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-blue-900">Complete Your Setup</CardTitle>
            <CardDescription className="text-blue-700">
              Finish setting up your store to unlock all features
            </CardDescription>
          </div>
          <Badge className={getStatusColor(tenant.onboarding_status)}>
            {getStatusIcon(tenant.onboarding_status)}
            <span className="ml-1 capitalize">{tenant.onboarding_status}</span>
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span>Setup Progress</span>
            <span>{getProgressPercentage()}%</span>
          </div>
          <Progress value={getProgressPercentage()} className="h-2" />
        </div>

        <div className="space-y-3">
          <div className="flex items-center space-x-3 p-3 bg-white rounded-lg">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Icon className="h-5 w-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-blue-900">{nextAction.title}</h4>
              <p className="text-sm text-blue-700">{nextAction.description}</p>
            </div>
            <Button
              size="sm"
              onClick={() => onActionClick?.(nextAction.action)}
            >
              Continue
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => onActionClick?.('products')}
          >
            <Package className="h-3 w-3 mr-1" />
            Add Products
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => onActionClick?.('customers')}
          >
            <Users className="h-3 w-3 mr-1" />
            Add Customers
          </Button>
        </div>
      </CardContent>
    </Card>
  )
} 